'use client'
import { throttle } from 'lodash'
import { useCallback, useEffect, useState } from 'react'
import {
  BiLogoInstagram,
  BiLogoWhatsapp,
  BiMenuAltRight,
  BiX,
} from 'react-icons/bi'
import { HiOutlineShoppingBag } from 'react-icons/hi2'

import { LINKS } from '@/config/constants'
import { useBreakpoints } from '@/hooks'
import { useCakeMakerState } from '@/store/useCakeMaker'

import { Button } from '../Button'
import { Logo } from '../Logo'
import { ThemeToggle } from '../ThemeToggle'
import { StoreOrdersModal } from './components/StoreOrdersModal'

import './styles.scss'

const NAVBAR_HEIGHT = 80

export function Navbar() {
  const { isBigger, isMedium } = useBreakpoints()
  const { storeOrders } = useCakeMakerState()
  const [isMenuActive, setIsMenuActive] = useState(false)
  const [activeSectionId, setActiveSectionId] = useState('hero')
  const [sections, setSections] = useState<NodeListOf<HTMLElement>>()
  const [listItems, setListItems] = useState<NodeListOf<Element>>()
  const [activeBulletLeftOffset, setActiveBulletLeftOffset] = useState(0)
  const [isStoreOrdersModalOpen, setIsStoreOrdersModalOpen] = useState(false)

  const handleScroll = throttle(() => {
    if (!sections) return
    const scrollPosition = window.scrollY + 200
    sections.forEach((section) => {
      const top = section.offsetTop
      const bottom = top + section.offsetHeight

      const isCurrentSection =
        scrollPosition >= top - NAVBAR_HEIGHT && scrollPosition <= bottom

      if (isCurrentSection && activeSectionId !== section.id)
        setActiveSectionId(section.id)
    })
  }, 500)

  const handleActiveBullet = useCallback(() => {
    if (!listItems) return
    const barWidth = 5
    const gap = isBigger ? 12 : 16
    let widthAcc = 0
    let activeIndex = listItems.length
    listItems.forEach((item, index) => {
      const isActive = !!item.querySelector(`.${activeSectionId}-link`)
      if (isActive) {
        activeIndex = index
        const lastItem = index === listItems.length - 1
        const additionalGap = lastItem ? 0 : gap + barWidth
        widthAcc += (item.clientWidth - additionalGap) / 2
      } else if (index < activeIndex) {
        widthAcc += item.clientWidth + gap
      }
    })
    setActiveBulletLeftOffset(widthAcc)
  }, [listItems, isBigger, activeSectionId])

  const scrollToSection = (
    e: React.MouseEvent<HTMLAnchorElement>,
    sectionId: string,
  ) => {
    const section = document.querySelector(`section#${sectionId}`)
    if (section) {
      e.preventDefault()
      setActiveSectionId(sectionId)
      toggleMenu()
      const rect = section.getBoundingClientRect()
      const offsetTop = rect.top + window.pageYOffset - NAVBAR_HEIGHT
      window.scrollTo({ top: offsetTop, behavior: 'smooth' })
    }
  }

  const toggleMenu = () => {
    setIsMenuActive((prev) => !prev)
  }

  useEffect(() => {
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [handleScroll])

  useEffect(() => {
    handleActiveBullet()
  }, [handleActiveBullet, isBigger, isMedium])

  useEffect(() => {
    setSections(document.querySelectorAll('section'))
    setListItems(document.querySelectorAll('.links__link'))
  }, [])

  return (
    <>
      <nav className="navbar" aria-label="Navegação principal">
        <div className="container content">
          <a
            href="#hero"
            aria-label="Sancta Fatia - ir para o início"
            className="navbar__logo-link"
          >
            <Logo color="var(--brand)" size={48} aria-hidden />
          </a>

          <div
            className={`menu ${isMedium ? 'mobile' : ''} ${isMenuActive ? 'active' : ''}`.trim()}
          >
            <Button
              text="Fechar"
              onClick={toggleMenu}
              leftRender={<BiX size={24} color="var(--w1)" />}
              className="close-menu-button"
              aria-label="Fechar menu"
            />

            <div className="menu-content">
              <div className="logo-menu-wrapper">
                <Logo color="var(--primary)" size={56} />
              </div>

              <div className="links-container">
                <ul className="links" role="list">
                  <li className="links__link">
                    <a
                      href="#hero"
                      onClick={(e) => scrollToSection(e, 'hero')}
                      id="hero-link"
                      className={
                        activeSectionId === 'hero' ? 'active hero-link' : ''
                      }
                      aria-current={
                        activeSectionId === 'hero' ? 'location' : undefined
                      }
                    >
                      início
                    </a>
                    {!isMedium && ' /'}
                  </li>
                  <li className="links__link">
                    <a
                      href="#cake-maker"
                      onClick={(e) => scrollToSection(e, 'cake-maker')}
                      id="cake-maker-link"
                      className={
                        activeSectionId === 'cake-maker'
                          ? 'active cake-maker-link'
                          : ''
                      }
                      aria-current={
                        activeSectionId === 'cake-maker'
                          ? 'location'
                          : undefined
                      }
                    >
                      encomende
                    </a>
                    {!isMedium && ' /'}
                  </li>
                  <li className="links__link">
                    <a
                      href="#flavors"
                      onClick={(e) => scrollToSection(e, 'flavors')}
                      id="flavors-link"
                      className={
                        activeSectionId === 'flavors'
                          ? 'active flavors-link'
                          : ''
                      }
                      aria-current={
                        activeSectionId === 'flavors' ? 'location' : undefined
                      }
                    >
                      sabores
                    </a>
                    {!isMedium && ' /'}
                  </li>
                  <li className="links__link">
                    <a
                      href="#about"
                      onClick={(e) => scrollToSection(e, 'about')}
                      id="about-link"
                      className={
                        activeSectionId === 'about' ? 'active about-link' : ''
                      }
                      aria-current={
                        activeSectionId === 'about' ? 'location' : undefined
                      }
                    >
                      nós
                    </a>
                    {!isMedium && ' /'}
                  </li>
                  <li className="links__link">
                    <a
                      href="#faq"
                      onClick={(e) => scrollToSection(e, 'faq')}
                      id="faq-link"
                      className={
                        activeSectionId === 'faq' ? 'active faq-link' : ''
                      }
                      aria-current={
                        activeSectionId === 'faq' ? 'location' : undefined
                      }
                    >
                      faq
                    </a>
                  </li>
                </ul>

                {!isMedium && (
                  <div
                    className="active-bullet"
                    style={{ left: `${activeBulletLeftOffset}px` }}
                  />
                )}
              </div>
            </div>

            {isMedium && (
              <div className="redirects">
                <div className="social-links">
                  <ThemeToggle />

                  <a
                    className="social-links__link"
                    href={LINKS.INSTAGRAM_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Abrir perfil da Sancta Fatia no Instagram"
                  >
                    <BiLogoInstagram size={32} aria-hidden />
                  </a>

                  <a
                    className="social-links__link"
                    href={LINKS.ORDER_WHATSAPP_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Encomendar pelo WhatsApp"
                  >
                    <BiLogoWhatsapp size={32} color="#25d366" aria-hidden />
                  </a>
                </div>
              </div>
            )}
          </div>

          <div className="redirects">
            <div className="social-links">
              <ThemeToggle />

              {storeOrders.length > 0 && (
                <button
                  type="button"
                  className="social-links__link store-orders-button"
                  onClick={() => setIsStoreOrdersModalOpen(true)}
                  aria-label="Ver histórico de pedidos"
                >
                  <HiOutlineShoppingBag size={28} aria-hidden />
                </button>
              )}

              <a
                className="social-links__link"
                href={LINKS.INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Abrir perfil da Sancta Fatia no Instagram"
              >
                <BiLogoInstagram size={32} aria-hidden />
              </a>

              <a
                className="social-links__link"
                href={LINKS.ORDER_WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Encomendar pelo WhatsApp"
              >
                <BiLogoWhatsapp size={32} color="#25d366" aria-hidden />
              </a>
            </div>

            {isMedium && (
              <Button
                onClick={toggleMenu}
                variant="primary"
                className="menu-button"
                aria-label="Abrir menu"
                rightRender={<BiMenuAltRight size={32} />}
              />
            )}
          </div>
        </div>
      </nav>

      <StoreOrdersModal
        isOpen={isStoreOrdersModalOpen}
        onClose={() => setIsStoreOrdersModalOpen(false)}
      />
    </>
  )
}
